<script setup lang="ts">
import { MultiSelect } from '@collector/shared-ui'
import { Incident } from '@collector/sportsapi-client'
import {
  useEventIncidentsQueue,
  useIncidentPlayers,
} from '@desktop/views/Relation/Sports/composables'
import { injectEventInfoConfig } from '@desktop/views/Relation/Sports/provide-inject'
import { IncidentPlayerOptionValue } from '@desktop/views/Relation/types'

import { isPlayerSelectOptionMatched } from '../../../utils'

interface Props {
  incident: Incident
}

const props = defineProps<Props>()

// inject
const eventInfoConfig = injectEventInfoConfig()

// data

/**
 * not reactive on purpose
 * 1. if event info is opened, and this component is initialized, do not open the popover
 * 2. if this component was initialized and player select was closed,
 *   then event info popover would be opened - do not 'reopen' select
 */
const open = !eventInfoConfig?.isEventInfoPopoverOpen.value

// composables
const eventIncidentsQueue = useEventIncidentsQueue()
const { getIncidentPlayersOptions } = useIncidentPlayers()

// state
const playersOptions = getIncidentPlayersOptions(props.incident)

// methods
function updatePlayer(playerValue: IncidentPlayerOptionValue): void {
  const payload = {
    participant_id: playerValue.id ?? null,
    participant_name: playerValue.shortName ?? null,
    participant_team_id: props.incident.participant_team_id,
  }

  eventIncidentsQueue.updateIncident(payload, props.incident)
}
</script>

<template>
  <MultiSelect
    placeholder="Assign player"
    :options="playersOptions"
    withFilter
    withOptionsFullWidth
    :filterPredicate="isPlayerSelectOptionMatched"
    :open
    @update:modelValue="updatePlayer($event as IncidentPlayerOptionValue)"
  >
    <template #option="{ option }">
      <div class="whitespace-nowrap">
        {{ option.value.shortName }}
      </div>
      <div class="text-2xs">
        {{ option.metadata.position }}
        <span
          v-if="option.metadata.shirtNr"
          class="font-bold"
        >
          {{ option.metadata.shirtNr }}
        </span>
      </div>
    </template>
  </MultiSelect>
</template>
